import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { CreditCard, Globe, LayoutDashboard, LogOut, Menu, Settings2, ShieldCheck, Sparkles, Users2, Wallet } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function AdminNavItem({ to, icon: Icon, label, end, onClick, testId }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      data-testid={testId}
      className={({ isActive }) =>
        [
          "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
          isActive
            ? "bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30"
            : "text-slate-300 hover:bg-slate-800/70 hover:text-white",
        ].join(" ")
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

function AdminSidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  function doLogout() {
    logout();
    nav("/admin/login", { replace: true });
  }

  return (
    <aside
      className="flex h-full w-64 flex-col border-e border-slate-800 bg-slate-950"
      data-testid="admin-sidebar"
    >
      <div className="flex items-center gap-3 border-b border-slate-800 px-5 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500 text-slate-950">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-white">Platform Admin</div>
          <div className="truncate text-xs text-slate-400">InvoicePro control plane</div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 p-3">
        <AdminNavItem
          to="/admin"
          end
          icon={LayoutDashboard}
          label="Dashboard"
          onClick={onNavigate}
          testId="admin-nav-dashboard"
        />
        <AdminNavItem
          to="/admin/tenants"
          icon={Users2}
          label="Tenants"
          onClick={onNavigate}
          testId="admin-nav-tenants"
        />
        <AdminNavItem
          to="/admin/subscriptions"
          icon={Sparkles}
          label="Subscriptions"
          onClick={onNavigate}
          testId="admin-nav-subscriptions"
        />
        <AdminNavItem
          to="/admin/payments"
          icon={Wallet}
          label="Payments"
          onClick={onNavigate}
          testId="admin-nav-payments"
        />
        <AdminNavItem
          to="/admin/platform-settings"
          icon={CreditCard}
          label="Platform / Stripe"
          onClick={onNavigate}
          testId="admin-nav-platform-settings"
        />
        <AdminNavItem
          to="/admin/landing"
          icon={Globe}
          label="Landing CMS"
          onClick={onNavigate}
          testId="admin-nav-landing"
        />
        <AdminNavItem
          to="/admin/settings"
          icon={Settings2}
          label="Settings"
          onClick={onNavigate}
          testId="admin-nav-settings"
        />
      </nav>

      <div className="border-t border-slate-800 p-4">
        <div className="mb-3 text-xs text-slate-400">
          <div className="truncate text-slate-300">{user?.name}</div>
          <div className="truncate">{user?.email}</div>
        </div>
        <button
          onClick={doLogout}
          data-testid="admin-logout"
          className="flex w-full items-center gap-2 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-200 hover:border-amber-500/50 hover:bg-slate-800 hover:text-amber-300"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </aside>
  );
}

export default function AdminLayout() {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="flex min-h-screen bg-[#0b1220] text-slate-100"
      data-testid="admin-layout"
    >
      {/* Desktop sidebar */}
      <div className="hidden md:block">
        <AdminSidebar />
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 start-0 w-64">
            <AdminSidebar onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile topbar */}
        <div className="flex items-center justify-between border-b border-slate-800 bg-slate-950 px-4 py-3 md:hidden">
          <button
            onClick={() => setOpen(true)}
            data-testid="admin-menu-btn"
            className="rounded-md p-1.5 text-slate-300 hover:bg-slate-800"
            aria-label="menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-amber-400" />
            <span className="text-sm font-semibold text-white">Platform Admin</span>
          </div>
          <div className="w-6" />
        </div>

        <main className="flex-1 overflow-x-hidden px-4 py-6 sm:px-6 md:px-10 md:py-10" data-testid="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
